// src/features/pages/admin/JobTitlesPage.tsx
// Company-admin Job Titles catalog. Live CRUD against employee-service
// (tenant-scoped server-side). Employees keep their title as the free-text
// `position`; this is the admin-defined suggestion list (catalog + free-text).
// Gated by department:write (same admin audience as Departments).
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import toast from 'react-hot-toast';
import { Briefcase, Plus, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { TruncatedText } from '@/features/shared/components/ui/TruncatedText';
import {
  getJobTitles,
  createJobTitle,
  updateJobTitle,
  deleteJobTitle,
  type JobTitle,
} from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { RequirePermission } from '@/rbac/RequirePermission';

const JobTitlesInner: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const canWrite = useHasPermission('department:write');
  const [titles, setTitles] = useState<JobTitle[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Inline create/edit form
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<JobTitle | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getJobTitles();
      setTitles(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('job_titles.load_error')));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setFormOpen(true);
  };
  const openEdit = (t: JobTitle) => {
    setEditing(t);
    setName(t.name);
    setDescription(t.description ?? '');
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t('common.name_required'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateJobTitle(editing.id, { name: trimmed, description: description.trim() || null });
        toast.success(t('job_titles.updated'));
      } else {
        await createJobTitle({ name: trimmed, description: description.trim() || undefined });
        toast.success(t('job_titles.created'));
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, editing ? t('job_titles.update_error') : t('job_titles.create_error')), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (jt: JobTitle) => {
    if (!(await confirm({ title: 'Delete job title', message: t('job_titles.delete_confirm', { name: jt.name }), confirmLabel: 'Delete', danger: true }))) return;
    try {
      await deleteJobTitle(jt.id);
      setTitles((prev) => prev.filter((x) => x.id !== jt.id));
      toast.success(t('job_titles.deleted'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('job_titles.delete_error')), { duration: 6000 });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-h2 text-charcoal">{t('job_titles.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('job_titles.subtitle')}
          </p>
        </div>
        {canWrite && !formOpen && (
          <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" /> {t('job_titles.new')}
          </Button>
        )}
      </div>

      {formOpen && (
        <Card className="border-orange-200 dark:border-orange-500/30">
          <CardHeader className="rounded-t-xl flex-row items-center">
            <CardTitle className="flex-1 text-orange-700">
              {editing ? t('job_titles.edit_title') : t('job_titles.new_title')}
            </CardTitle>
            <button type="button" onClick={closeForm} aria-label={t('common.close')} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="title-name">{t('job_titles.field_title')}</label>
              <input
                id="title-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('job_titles.placeholder_title')}
                className="mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="title-desc">{t('common.description')}</label>
              <textarea
                id="title-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder={t('common.optional')}
                className="mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400 resize-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={save} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {editing ? t('common.save_changes') : t('job_titles.create')}
              </Button>
              <Button variant="outline" onClick={closeForm} disabled={saving}>{t('common.cancel')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-orange-100 dark:border-orange-500/20">
        <CardHeader className="rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Briefcase className="w-5 h-5" /> {t('job_titles.all')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : titles.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">{t('job_titles.empty')}</div>
          ) : (
            <ul className="divide-y divide-border">
              {titles.map((jt) => (
                <li key={jt.id} className="py-3 flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
                    <Briefcase className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground">{jt.name}</div>
                    {jt.description && <TruncatedText className="text-sm text-muted-foreground">{jt.description}</TruncatedText>}
                  </div>
                  {typeof jt.employee_count === 'number' && jt.employee_count > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {jt.employee_count} {jt.employee_count === 1 ? t('job_titles.person') : t('job_titles.people')}
                    </span>
                  )}
                  {jt.is_active === false && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t('common.inactive')}</span>
                  )}
                  {canWrite && (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => openEdit(jt)} aria-label={t('common.edit', { name: jt.name })}
                        className="p-1.5 rounded-[8px] text-muted-foreground hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/[0.12]">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => remove(jt)} aria-label={t('common.delete', { name: jt.name })}
                        className="p-1.5 rounded-[8px] text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/[0.12]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const JobTitlesPage: React.FC = () => (
  <RequirePermission perm="department:write">
    <JobTitlesInner />
  </RequirePermission>
);

export default JobTitlesPage;
